import { queryOptions, useQuery } from '@tanstack/react-query';
import { IStore } from '../../../interfaces/StoreTypes';
import { useSelector } from 'react-redux';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import type { ICustomConfigurationService } from '@intigral/domain/lib/application/services/CustomConfigurationService';
import type { ProvidersItem } from '@intigral/domain/lib/entity/interface/iGetProviders';
import { useSearchParams } from 'react-router-dom';

export const useProviders = () => {
    const { selectedProfile } = useSelector((state: IStore) => state.profile);
    const [searchParams] = useSearchParams();

    const providerListQueryOptions = ({
        dataSource,
        isKidsMode,
        profileName,
    }: {
        dataSource: string;
        isKidsMode: boolean;
        profileName: string;
    }) =>
        queryOptions({
            queryKey: ['header-menu', 'providers', profileName],
            queryFn: async () => {
                const commonService = container.resolve<ICustomConfigurationService>(
                    DI.SERVICES.ICustomConfigurationService
                );
                const response = await commonService.getProviders(dataSource);
                return response;
            },
            select: (response) => {
                const data = response?.data.filter((provider) => !isKidsMode || provider.kidsMode) || [];

                const data_v1 = response?.data_v1
                    ? response.data_v1.filter((provider) => provider.enable && (!isKidsMode || provider.kidsMode))
                    : [];

                const combinedData = [...data_v1, ...data].reduce((acc: ProvidersItem[], current) => {
                    const existing = acc.find((item) => item.id === current.id);
                    if (!existing) {
                        acc.push(current);
                    }
                    return acc;
                }, []);

                return combinedData;
            },
            staleTime: 60 * 60 * 1000, // 1 hour
            gcTime: 60 * 60 * 1000, // keep cache for 1 hour
            retry: 2,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        });

    const { data: providersData } = useQuery(
        providerListQueryOptions({
            dataSource: 'home_carousel_providers_v2',
            isKidsMode: selectedProfile.kidsMode,
            profileName: selectedProfile.profileName,
        })
    );
    const currentProviderId = searchParams.get('providerId');

    const currentProviderLink =
        currentProviderId &&
        Array.isArray(providersData) &&
        providersData.find((provider) => provider.id.toLowerCase() === currentProviderId.toLowerCase());

    return {
        currentProviderLink,
    };
};
